import React, { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import {
  DataGridPremium,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import { useDispatch } from 'react-redux';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatOptionalDayjs } from '@/utils/date';
import { AccountBase, AccountViewModel } from '@/models/AccountViewModel';
import {
  useGetAccountsQuery,
  useReactivateAccountMutation,
} from '@/store/accountsApi';
import { holdingsApi } from '@/store/holdingsApi';
import { transactionsApi } from '@/store/transactionsApi';
import { cashFlowApi } from '@/store/cashFlowApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { skipToken } from '@reduxjs/toolkit/query';
import { useLocalization } from '@/hooks/useLocalization';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import { useGlobalSnackbar } from '@/contexts/snackContext';
import { actionColumnConfig } from '@/components/grids/gridColumnConfigs';
import { GridContainer } from '../../../components/grids/GridContainer';

interface ClosedAccountsModalProps {
  open: boolean;
  onClose: () => void;
  onDelete: (account: AccountBase) => void;
  onAccountSelect: (account: AccountBase) => void;
  deletedAccountId?: number | null;
}

interface ClosedAccountActionsMenuProps {
  account: AccountBase;
  onReactivate: (account: AccountBase) => void;
  onDelete: (account: AccountBase) => void;
}

const formatAccountType = (account: AccountBase) =>
  [account.ClassificationName, account.SubClassificationName]
    .filter((value) => value && value.toLowerCase() !== 'not set')
    .join(' / ') || 'N/A';

const isActionableClosedAccount = (account: AccountBase) =>
  account.CanUnClose === true;

const NoClosedAccountsOverlay = () => {
  const { t } = useLocalization();

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography>
        {t('closedAccounts.noClosedAccounts', 'No closed accounts to display')}
      </Typography>
    </Box>
  );
};

const ClosedAccountActionsMenu: React.FC<ClosedAccountActionsMenuProps> = ({
  account,
  onReactivate,
  onDelete,
}) => {
  const { t } = useLocalization();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const canAct = isActionableClosedAccount(account);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label={t('moreOptions', 'More Options')}
        aria-controls={open ? 'closed-account-actions-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id='closed-account-actions-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {canAct ? (
          <>
            <MenuItem
              onClick={() => {
                onReactivate(account);
                handleClose();
              }}
            >
              {t('closedAccounts.reactivateAccount', 'Reactivate Account')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                onDelete(account);
                handleClose();
              }}
            >
              {t('closedAccounts.deleteAccount', 'Delete Account')}
            </MenuItem>
          </>
        ) : (
          <Box sx={{ maxWidth: 260, px: 2, py: 1.25 }}>
            <Typography variant='body2'>
              {t(
                'closedAccounts.firmManagedMessage',
                'You cannot delete or reactivate firm-managed accounts.',
              )}
            </Typography>
          </Box>
        )}
      </Menu>
    </>
  );
};

const ClosedAccountsModal: React.FC<ClosedAccountsModalProps> = ({
  open,
  onClose,
  onDelete,
  onAccountSelect,
  deletedAccountId,
}) => {
  const { t } = useLocalization();
  const { showSnackbar } = useGlobalSnackbar();
  const dispatch = useDispatch();
  const authLoaded = useAuthIsLoaded();
  const [searchTerm, setSearchTerm] = useState('');
  const [reactivationAccount, setReactivationAccount] =
    useState<AccountBase | null>(null);
  const [locallyRemovedAccountIds, setLocallyRemovedAccountIds] = useState<
    number[]
  >([]);

  React.useEffect(() => {
    if (deletedAccountId == null) {
      return;
    }

    setLocallyRemovedAccountIds((current) =>
      current.includes(deletedAccountId)
        ? current
        : [...current, deletedAccountId],
    );
  }, [deletedAccountId]);

  const {
    data: accountsData,
    isLoading,
    isFetching,
  } = useGetAccountsQuery(authLoaded ? { showClosed: true } : skipToken);
  const [reactivateAccount, { isLoading: isReactivating }] =
    useReactivateAccountMutation();

  // RTK's inferred query result widens `data` to `any` here; narrow it back to the
  // real response type so member access stays type-safe.
  const rows = useMemo(
    () =>
      ((accountsData as AccountViewModel | undefined)?.Records ?? []).filter(
        (account) =>
          Boolean(account.ClosedDate) &&
          !locallyRemovedAccountIds.includes(account.AccountId),
      ),
    [accountsData, locallyRemovedAccountIds],
  );

  const filterModel = useMemo<GridFilterModel>(
    () => ({
      items: searchTerm
        ? [
            {
              id: 1,
              field: 'Name',
              operator: 'contains',
              value: searchTerm,
            },
          ]
        : [],
    }),
    [searchTerm],
  );

  const handleReactivateConfirm = () => {
    if (!reactivationAccount) return;

    reactivateAccount(reactivationAccount)
      .unwrap()
      .then(() => {
        setLocallyRemovedAccountIds((current) => [
          ...current,
          reactivationAccount.AccountId,
        ]);
        setReactivationAccount(null);
        dispatch(holdingsApi.util.invalidateTags(['Holdings']));
        dispatch(
          transactionsApi.util.invalidateTags([
            'TransactionTrends',
            'Transactions',
            'BrokerageTransactions',
            'BankTransactionsOverview',
            'BankTransactions',
            'WizardEligibility',
          ]),
        );
        dispatch(cashFlowApi.util.invalidateTags(['CashFlowCategories']));
        showSnackbar(
          t('closedAccounts.reactivateSuccess', 'Account reactivated.'),
        );
      })
      .catch(() => {
        showSnackbar(
          t(
            'closedAccounts.reactivateError',
            "We couldn't reactivate this account. Please try again.",
          ),
        );
      });
  };

  const columns = useMemo<GridColDef<AccountBase>[]>(
    () => [
      {
        field: 'Name',
        headerName: t('name', 'Name'),
        flex: 1.4,
        minWidth: 220,
        renderCell: (params: GridRenderCellParams<AccountBase>) => (
          <Link
            component='button'
            underline='hover'
            onClick={(event) => {
              event.stopPropagation();
              onAccountSelect(params.row);
            }}
            sx={{
              minWidth: 0,
              textAlign: 'left',
              color: 'primary.main',
              font: 'inherit',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.row.Name}
          </Link>
        ),
      },
      {
        field: 'accountType',
        headerName: t('grid.accountType', 'Account Type'),
        flex: 1,
        minWidth: 170,
        valueGetter: (_value, row) => formatAccountType(row),
        filterable: false,
      },
      {
        field: 'LastUpdated',
        headerName: t('grid.lastUpdated', 'Last Updated'),
        flex: 0.8,
        minWidth: 140,
        renderCell: (params: GridRenderCellParams<AccountBase>) =>
          formatOptionalDayjs(params.row.LastUpdated, 'M/D/YYYY', ''),
        filterable: false,
      },
      {
        field: 'Balance',
        headerName: t('grid.balance', 'Balance'),
        type: 'number',
        flex: 0.8,
        minWidth: 140,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => formatNumberToCurrency(value),
        filterable: false,
      },
      {
        field: 'ClosedDate',
        headerName: t('closedAccounts.closeDate', 'Close Date'),
        flex: 0.8,
        minWidth: 140,
        renderCell: (params: GridRenderCellParams<AccountBase>) =>
          formatOptionalDayjs(params.row.ClosedDate, 'M/D/YYYY', ''),
        filterable: false,
      },
      {
        field: 'actions',
        ...actionColumnConfig,
        width: 64,
        renderCell: (params: GridRenderCellParams<AccountBase>) => (
          <ClosedAccountActionsMenu
            account={params.row}
            onReactivate={setReactivationAccount}
            onDelete={onDelete}
          />
        ),
      },
    ],
    [onAccountSelect, onDelete, t],
  );

  const reactivationCopy = reactivationAccount
    ? reactivationAccount.AccountType === 2
      ? t(
          'closedAccounts.reactivateManualCopy',
          `This account will be restored to your portfolio with its last recorded balance of ${formatNumberToCurrency(
            reactivationAccount.Balance,
          )}. You can edit the balance after reactivation from the Accounts page.`,
        )
      : t(
          'closedAccounts.reactivateAggregatedCopy',
          "This account will be restored to your portfolio. We'll attempt to reconnect automatically. If the connection can't be reestablished, you can reconnect manually from the Accounts page.",
        )
    : '';

  return (
    <>
      {open ? (
        <Dialog
          open
          onClose={onClose}
          maxWidth='lg'
          fullWidth
          slotProps={{
            paper: {
              sx: {
                maxHeight: '85vh',
              },
            },
          }}
        >
          <DialogTitle
            component='div'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pr: 1,
            }}
          >
            <Typography
              variant='h6'
              component='p'
              sx={{ m: 0 }}
            >
              {t('closedAccounts.title', 'Closed Accounts')}
            </Typography>
            <IconButton
              aria-label={t('close', 'Close')}
              onClick={onClose}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Stack
              spacing={2}
              sx={{ minHeight: 0 }}
            >
              <TextField
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                }}
                placeholder={t('search', 'Search')}
                size='small'
                sx={{ width: { xs: '100%', sm: 280 } }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon fontSize='small' />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Box
                sx={{
                  width: '100%',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus':
                    {
                      outline: 'none',
                    },
                }}
              >
                <GridContainer
                  isWidget={false}
                  size='flyout'
                >
                  <DataGridPremium
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.AccountId}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    disableColumnFilter
                    disableColumnSelector
                    disableDensitySelector
                    disablePivoting
                    filterModel={filterModel}
                    loading={Boolean(isLoading || isFetching)}
                    slots={{
                      noRowsOverlay: NoClosedAccountsOverlay,
                    }}
                    slotProps={{
                      loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'skeleton',
                      },
                    }}
                    sx={{
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                      },
                    }}
                  />
                </GridContainer>
              </Box>
            </Stack>
          </DialogContent>
        </Dialog>
      ) : null}

      <ConfirmationDialog
        open={!!reactivationAccount}
        onClose={() => {
          setReactivationAccount(null);
        }}
        onConfirm={handleReactivateConfirm}
        title={t('closedAccounts.reactivateTitle', 'Reactivate Account')}
        contentText={reactivationCopy}
        confirmButtonText={t('closedAccounts.reactivateCta', 'Reactivate')}
        cancelButtonText={t('cancel', 'Cancel')}
        confirmButtonColor='primary'
        isLoading={isReactivating}
      />
    </>
  );
};

export default ClosedAccountsModal;
